import React, { useContext } from "react";
import UserContext from "contexts/UserContext";
import getRelatedArticles from "utils/ContentfulApi/getRelatedArticles";
import Head from "next/head";
import { NextSeo } from "next-seo";
import Container from "react-bootstrap/Container";

import Post from "components/Post";
import ContentfulApi from "@utils/ContentfulApi";
import ContentfulClient from "@utils/ContentfulApi/ContentfulClient";
import { getArticleCardHref } from "utils/helpers";



export default function canonicalUrl(props) {
	const {
		post,
		// childTags,
		relatedArticles,
	} = props;
	const bannerImage = post?.fields?.bannerImage?.fields?.file?.url
		? `https:${post?.fields.bannerImage?.fields?.file?.url}`
		: "https://images.ctfassets.net/mdag2z2o3c18/73533DQHZgyUDdrQ0j4iwo/d2ac1fd3ae5f15103e3ce6f107e3d7cb/aapl-logo.png";

	return (
		<>
			<NextSeo
				title={`${post.fields.title} | AAPL Publication`}
				description={post.fields.summary || post.fields.abstract}
				openGraph={{
					images: [
						{
							url: bannerImage,
						},
					],
					url: `${process.env.WEB_URL}${getArticleCardHref(post.fields)}`,
					title: post.fields.title,
					description: post.fields.summary || post.fields.abstract,
				}}
			/>
			<Head>
				<meta property="twitter:title" content={post.fields.title} />
				<meta name="twitter:image" content={bannerImage} />
				<meta name="twitter:description" content={post.fields.summary || post.fields.abstract} />
			</Head>
			<Container fluid className="">
				<Post
					post={post}
					//  childTags={childTags}
					relatedArticles={relatedArticles}
				/>
				<style global jsx>{`
					body {
						background-color: white;
					}
				`}</style>
			</Container>
		</>
	);
}

const textLength = (nodes, len = 0) => {
	for (const n of nodes) {
		if (n.nodeType === "text") {
			len += n.value.length;
		}
		else {
			if (n.content) {
				len = textLength(n.content, len);
			}
		}
	}

	return len;
}

const insertIndex = (nodes, percent) => {
	if (percent === 0) {
		return 0;
	}

	const totalLength = textLength(nodes);
	let len = 0;
	let idx = 1;

	for (const n of nodes) {
		if (n.content) {
			len += textLength(n.content);
			if (len / totalLength > percent) {
				return idx;
			}
			else {
				idx += 1;
			}
		}
	}

	return nodes.length - 1;
}


/*
Structure of the object

<adsListNode> {
	nodeType: "embedded-entry-block",
	content: [],
	data: {
		target: <adsList> {
			fields: {
				percentOfTextToSkip,
				secondsPerAdvertisement,
				advertisements: [
					{
						fields: {
							internalName: "",
							url: "",
							image: {
								fields: {
									file: {
										url: ""
									}
								}
							}
						}
					}
				]
			}
		}
	},
} 
 */

const adsListLength = (adsList) => adsList?.fields?.advertisements?.length ?? 0;

const filterAdsByDates = (post) => {
	const nodes = post.fields.bodyText.content;
	const adsListNode = nodes.find(x => x.nodeType === "embedded-entry-block");
	const adsList = adsListNode?.data?.target;
	if (adsListLength(adsList) > 0) {
		const today = new Date();
		today.setUTCHours(0, 0, 0, 0);

		adsList.fields.advertisements = adsList.fields.advertisements.filter(x => {
			const startDate = new Date(x.fields.startDate);
			startDate.setUTCHours(0, 0, 0, 0);

			let valid = today >= startDate;

			if (valid && x.fields.endDate) {
				const endDate = new Date(x.fields.endDate);
				endDate.setUTCHours(0, 0, 0, 0);
				valid = today <= endDate;
			}

			return valid;
		});
	}

	if (adsListLength(adsList) === 0) {
		var idx = nodes.indexOf(adsListNode);
		nodes.splice(idx, 1);
	}
}

const addAdsList = (post, defaultAdsList, highPriorityAdsList) => {
	const nodes = post.fields.bodyText.content;
	let adsListNode = nodes.find(x => x.nodeType === "embedded-entry-block");
	if (!adsListNode) {
		const adsList = adsListLength(highPriorityAdsList) > 0 ? highPriorityAdsList : defaultAdsList;
		if (adsListLength(adsList) > 0) {
			const percentOfTextToSkip = adsList.fields.percentOfTextToSkip / 100;
			const idx = insertIndex(nodes, percentOfTextToSkip);

			adsListNode = {
				nodeType: "embedded-entry-block",
				data: { target: adsList },
				content: [],
			};

			nodes.splice(idx, 0, adsListNode);
		}
	}

	if (adsListNode) {
		filterAdsByDates(post);
	}
}




export async function getServerSideProps({ params, res }) {
	const post = await ContentfulApi.getBasicArticleBySlug(params.canonicalUrl);

	const defaultAdsList = await ContentfulClient.getIR_Entry(process.env.ADS_DEFAULT_ADSLIST_ID);
	const highPriorityAdsList = await ContentfulClient.getIR_Entry(process.env.ADS_HIGHPRIORITY_ADSLIST_ID);
	addAdsList(post, defaultAdsList, highPriorityAdsList);

	// Cache the response if desired (optional)
	// This will cache the page for the specified number of seconds
	if (process.env.REVALIDATE_TIMER) {
		res.setHeader("Cache-Control", `s-maxage=${10}, stale-while-revalidate`);
	}

	//some articles maybe published, BUT publication date is
	//in the future, show 404 if this is the case
	const now = new Date().getTime();
	const notYetPublished = !post?.fields?.publicationDate
		? true
		: now < new Date(post.fields.publicationDate).getTime()
		? true
		: false;

	if (!post || notYetPublished) {
		return {
			notFound: true,
		};
	}

	//check if has DOI
	if (post?.fields?.doiPrefix && post?.fields?.doiSuffix) {
		return {
			redirect: {
				destination: getArticleCardHref(post.fields),
				permanent: false, // Set to true if this is a permanent redirect
			},
		};
	}

	let relatedArticles = await getRelatedArticles(post);

	return {
		props: {
			post,
			relatedArticles,
		},
	};
}
